import { FC, useState } from 'react'

import { Check16, Copy16 } from '@makyoapp/makyo-icon/icon'
import { Modal } from '@makyoapp/makyo-ui'

interface DnsConfigInstructionModalProps {
  showDnsConfig: boolean
  setShowDnsConfig: (showDnsConfig: boolean) => void
}

export const DnsConfigInstructionModal: FC<DnsConfigInstructionModalProps> = ({ showDnsConfig, setShowDnsConfig }) => {
  const [copied, setCopied] = useState(false)

  const copyIP = () => {
    navigator.clipboard.writeText('173.249.52.75')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Modal show={showDnsConfig} onHide={() => setShowDnsConfig(false)}>
      <Modal.Body className='max-w-4xl modal-lg max-h-[calc(100vh-50px)] overflow-y-auto'>
        <div className='bg-linear-to-b from-background to-background/50 px-4 py-12 dark:from-gray-900 dark:to-gray-950 -m-4'>
          <div className='max-w-3xl mx-auto'>
            <div className='space-y-6'>
              {/* Header section */}
              <div className='text-center space-y-4'>
                <div className='inline-block px-3 py-1 bg-teal-50 text-teal-800 text-xs font-medium rounded-full dark:bg-teal-900/30 dark:text-teal-300'>
                  DNS Configuration Guide
                </div>
                <h1 className='text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1F2C] tracking-tight dark:text-white'>
                  Configuring DNS for Your Makyo Website
                </h1>
                <p className='text-neutral-600 md:text-lg max-w-2xl mx-auto dark:text-neutral-300'>
                  This guide will help you set up your DNS configurations to publish your app or website using
                  <span className='font-semibold'> Makyo</span>, a no-code app/website builder powered by AI.
                </p>
              </div>

              <hr className='shrink-0 bg-border h-[1px] w-full my-8 dark:border-gray-700' />

              {/* Main content */}
              <div className='rounded-lg border text-card-foreground p-6 md:p-8 shadow-xs bg-white border-gray-100 dark:bg-gray-800/50 dark:border-gray-700'>
                <h2 className='text-xl md:text-2xl font-semibold text-[#1A1F2C] mb-6 dark:text-white'>
                  Steps to Configure Your DNS
                </h2>

                <div className='space-y-10'>
                  {/* Step 1 */}
                  <div className='space-y-4'>
                    <div className='flex items-center'>
                      <div className='shrink-0 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center text-[#9b87f5] font-medium dark:bg-teal-900/50 dark:text-teal-300'>
                        1
                      </div>
                      <h3 className='ml-4 text-lg font-semibold text-[#1A1F2C] dark:text-white'>
                        Access Your DNS Manager
                      </h3>
                    </div>
                    <p className='text-gray-700 dark:text-gray-300'>
                      Log in to your domain registrar or hosting provider where you manage your domain settings.
                    </p>
                  </div>

                  {/* Step 2 */}
                  <div className='space-y-4'>
                    <div className='flex items-center'>
                      <div className='shrink-0 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center text-[#9b87f5] font-medium dark:bg-teal-900/50 dark:text-teal-300'>
                        2
                      </div>
                      <h3 className='ml-4 text-lg font-semibold text-[#1A1F2C] dark:text-white'>
                        Navigate to Your DNS Zone
                      </h3>
                    </div>
                    <p className='text-gray-700 dark:text-gray-300'>
                      Find the <span className='font-medium'>DNS Zone</span> or{' '}
                      <span className='font-medium'>DNS Settings</span> section. This is where you can manage DNS
                      records for your domain.
                    </p>
                  </div>

                  {/* Step 3 */}
                  <div className='space-y-4'>
                    <div className='flex items-center'>
                      <div className='shrink-0 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center text-[#9b87f5] font-medium dark:bg-teal-900/50 dark:text-teal-300'>
                        3
                      </div>
                      <h3 className='ml-4 text-lg font-semibold text-[#1A1F2C] dark:text-white'>
                        Add an{' '}
                        <code className='text-[#8B5CF6] bg-teal-50 px-1.5 py-0.5 rounded-sm text-sm dark:bg-teal-900/30 dark:text-teal-300'>
                          A
                        </code>{' '}
                        Record
                      </h3>
                    </div>
                    <div className='ml-12 space-y-6'>
                      <p className='text-gray-700 dark:text-gray-300'>
                        To point your domain to <span className='font-medium'>Makyo&apos;s servers</span>, create an{' '}
                        <code className='text-[#8B5CF6] bg-teal-50 px-1.5 py-0.5 rounded-sm text-sm dark:bg-teal-900/30 dark:text-teal-300'>
                          A
                        </code>{' '}
                        record with the following details:
                      </p>

                      <div className='bg-gray-50 rounded-lg p-4 border border-gray-200 dark:bg-gray-800 dark:border-gray-700'>
                        <div className='grid grid-cols-[120px_1fr] gap-y-3 text-sm'>
                          <div className='text-gray-500 dark:text-gray-400'>Host:</div>
                          <div className='font-medium text-gray-800 dark:text-gray-200'>
                            <code className='bg-white px-2 py-1 rounded-sm border border-gray-200 dark:bg-gray-700 dark:border-gray-600'>
                              @
                            </code>{' '}
                            (for the root domain) or your subdomain (e.g.,{' '}
                            <code className='bg-white px-2 py-1 rounded-sm border border-gray-200 dark:bg-gray-700 dark:border-gray-600'>
                              www
                            </code>
                            )
                          </div>

                          <div className='text-gray-500 dark:text-gray-400'>Type:</div>
                          <div className='font-medium text-gray-800 dark:text-gray-200'>
                            <code className='text-[#8B5CF6] bg-teal-50 px-1.5 py-0.5 rounded-sm text-sm dark:bg-teal-900/30 dark:text-teal-300'>
                              A
                            </code>
                          </div>

                          <div className='text-gray-500 dark:text-gray-400'>Value (IP):</div>
                          <div className='font-medium text-gray-800 dark:text-gray-200 flex items-center'>
                            <code className='bg-white px-2 py-1 rounded-sm border border-gray-200 dark:bg-gray-700 dark:border-gray-600'>
                              173.249.52.75
                            </code>
                            <button
                              onClick={copyIP}
                              className='ml-2 text-[#9b87f5] hover:text-[#8B5CF6] transition-colors'
                              aria-label='Copy IP address'
                            >
                              {copied ? <Check16 /> : <Copy16 />}
                            </button>
                          </div>

                          <div className='text-gray-500 dark:text-gray-400'>TTL:</div>
                          <div className='font-medium text-gray-800 dark:text-gray-200'>
                            Default (or set to the lowest available for faster propagation)
                          </div>
                        </div>
                      </div>

                      <p className='text-gray-700 dark:text-gray-300'>
                        If you also want to set up a subdomain (e.g.,{' '}
                        <code className='bg-gray-100 px-1.5 py-0.5 rounded-sm text-sm dark:bg-gray-700'>app.site.com</code>
                        ), create another{' '}
                        <code className='text-[#8B5CF6] bg-teal-50 px-1.5 py-0.5 rounded-sm text-sm dark:bg-teal-900/30 dark:text-teal-300'>
                          A
                        </code>{' '}
                        record:
                      </p>

                      <div className='bg-gray-50 rounded-lg p-4 border border-gray-200 dark:bg-gray-800 dark:border-gray-700'>
                        <div className='grid grid-cols-[120px_1fr] gap-y-3 text-sm'>
                          <div className='text-gray-500 dark:text-gray-400'>Host:</div>
                          <div className='font-medium text-gray-800 dark:text-gray-200'>
                            <code className='bg-white px-2 py-1 rounded-sm border border-gray-200 dark:bg-gray-700 dark:border-gray-600'>
                              app
                            </code>{' '}
                            (or your desired subdomain)
                          </div>

                          <div className='text-gray-500 dark:text-gray-400'>Type:</div>
                          <div className='font-medium text-gray-800 dark:text-gray-200'>
                            <code className='text-[#8B5CF6] bg-teal-50 px-2 py-1 rounded-sm dark:bg-teal-900/30 dark:text-teal-300'>
                              A
                            </code>
                          </div>

                          <div className='text-gray-500 dark:text-gray-400'>Value (IP):</div>
                          <div className='font-medium text-gray-800 dark:text-gray-200 flex items-center'>
                            <code className='bg-white px-2 py-1 rounded-sm border border-gray-200 dark:bg-gray-700 dark:border-gray-600'>
                              173.249.52.75
                            </code>
                          </div>

                          <div className='text-gray-500 dark:text-gray-400'>TTL:</div>
                          <div className='font-medium text-gray-800 dark:text-gray-200'>Default</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className='space-y-4'>
                    <div className='flex items-center'>
                      <div className='shrink-0 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center text-[#9b87f5] font-medium dark:bg-teal-900/50 dark:text-teal-300'>
                        4
                      </div>
                      <h3 className='ml-4 text-lg font-semibold text-[#1A1F2C] dark:text-white'>
                        Verify Your Domain in Makyo
                      </h3>
                    </div>
                    <div className='ml-12 space-y-4'>
                      <p className='text-gray-700 dark:text-gray-300'>Once the DNS records are added:</p>
                      <ol className='list-decimal pl-5 space-y-2 text-gray-700 dark:text-gray-300'>
                        <li>
                          Go back to your <span className='font-medium'>Makyo app settings</span>.
                        </li>
                        <li>
                          Enter your domain name (e.g.,{' '}
                          <code className='bg-gray-100 px-1.5 py-0.5 rounded-sm text-sm dark:bg-gray-700'>site.com</code>).
                        </li>
                        <li>
                          Click the <span className='font-medium'>Verify</span> button.
                        </li>
                      </ol>
                    </div>
                  </div>

                  {/* Step 5 */}
                  <div className='space-y-4'>
                    <div className='flex items-center'>
                      <div className='shrink-0 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center text-[#9b87f5] font-medium dark:bg-teal-900/50 dark:text-teal-300'>
                        5
                      </div>
                      <h3 className='ml-4 text-lg font-semibold text-[#1A1F2C] dark:text-white'>
                        DNS Propagation Time
                      </h3>
                    </div>
                    <div className='ml-12'>
                      <p className='text-gray-700 dark:text-gray-300'>
                        DNS changes may take <span className='font-medium'>a few minutes to an hour</span> to propagate.
                        If verification fails immediately after making changes, wait for some time and try again.
                      </p>
                      <div className='mt-4 p-4 bg-teal-50 rounded-lg border border-teal-100 dark:bg-teal-900/20 dark:border-teal-900/30'>
                        <p className='text-gray-700 dark:text-gray-300'>
                          Once your domain is successfully verified, publishing your app on Makyo will automatically
                          link it to your domain.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer note */}
              <div className='text-center text-gray-500 text-sm mt-8 pt-4 border-t border-gray-200 dark:text-gray-400 dark:border-gray-700'>
                For further assistance, check your domain provider&apos;s documentation on DNS management or contact
                their support.
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}
